import { all, apply, call, put, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import { push } from '@lagunovsky/redux-react-router'

import { LayoutActions } from '../layout'
import { AuthActions } from './index'
import { User } from '../../application/domain/models/auth/user'
import { UserType } from '../../application/domain/utils/user.type'
import authService from '../../services/auth'
import usersService from '../../services/users'
import { AccessToken } from '../../application/domain/models/auth/access.token'
import { AuthTypes, IActionAuth, IActionFirstLogin } from './types'
import { SnackbarActions, SnackBarMessageType } from '../snackbar'
import Messages from '../../assets/locales/pt/translation.json'

/**
 * <h5>Generator functions that make calls to the authentication service to obtain the data.</h5>
 * @see {@link https://developer.mozilla.org/pt-BR/docs/Web/JavaScript/Reference/Statements/function*}
 * @typedef AuthSagas
 * @namespace AuthSagas
 * @category React
 * @subcategory Redux / Sagas
 */

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof AuthSagas
 * @alias AuthSagas.authenticate
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionAuth>} action User triggered action data.
 */
export function* authenticate(action: PayloadAction<IActionAuth>) {
    const { loginSuccess, loginFailure } = AuthActions
    const { openSnackBar } = SnackbarActions
    try {
        const { login, password } = action.payload
        if (password) {
            yield apply(authService, authService.login, [login, password])
            yield put(loginSuccess())
            yield put(push(`/login/tfa`))
        }
    } catch (err: any) {
        yield put(loginFailure())
        if (err.message === 'Request failed with status code 401') {
            yield put(openSnackBar({
                title: 'Não autorizado.',
                type: SnackBarMessageType.WARNING,
                message: `${Messages.REQUEST.AUTH.INVALID}`
            }))
        }
        if (err.message === 'Request failed with status code 400') {
            yield put(openSnackBar({
                title: 'Email inválido.',
                type: SnackBarMessageType.WARNING,
                message: `${Messages.REQUEST.AUTH.EMAIL_NULL}`
            }))
        }
        if (err.message === 'An unexpected error has occurred. Please try again later...') {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

/**
 * Generator function that calls the service to load a users's data.
 * @memberof AuthSagas
 * @alias AuthSagas.getUserLogged
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {UserType} type Type of users.
 * @param {string} userId User identifier.
 */
export function* getUserLogged(type: UserType, userId: string) {
    const { changeUserName } = LayoutActions
    const { openSnackBar } = SnackbarActions
    try {
        const user: User = yield apply(usersService, usersService.getUserById, [type, userId])
        yield put(changeUserName({ username: `${user?.name}` }))
    } catch (err: any) {
        yield put(changeUserName({ username: `` }))
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* replacePassword(action: PayloadAction<IActionFirstLogin>) {
    const { replacePasswordSucess, replacePasswordFailure } = AuthActions
    const { openDialog, openSnackBar } = SnackbarActions
    const {
        new_password,
        password,
        email,
        token
    } = action.payload
    try {
        // @ts-ignore
        yield apply(authService, authService.changePassword, [email, password, new_password, token])
        yield put(replacePasswordSucess())
        // @ts-ignore
        yield call(authenticate, { payload: { login: email, password: new_password } })
        yield put(openDialog({
            title: 'Sucesso.',
            message: 'Nova senha atualizada com sucesso.',
            to: '/login'
        }))
    } catch (err: any) {
        yield put(replacePasswordFailure())
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* forgotPassword(action: PayloadAction<IActionFirstLogin>) {
    const { forgotPasswordSucess, forgotPasswordFailure } = AuthActions
    const { openSnackBar, openDialog } = SnackbarActions
    try {
        const {
            email
        } = action.payload
        // @ts-ignore
        yield apply(authService, authService.forgotPassword, [email])
        yield put(forgotPasswordSucess())
        yield put(openDialog({
            title: 'Sucesso.',
            message: 'Cheque a caixa de entrada e o SPAM do email informado para prosseguir com a recuperação da sua senha.'
        }))
    } catch (err: any) {
        yield put(forgotPasswordFailure())
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* loginTfa(action: PayloadAction<IActionAuth>) {
    const { loginTfaSuccess, loginTfaFailure } = AuthActions
    const { openSnackBar } = SnackbarActions
    try {
        const {
            login,
            code
        } = action.payload
        if (code) {
            const accessToken: AccessToken = yield apply(authService, authService.loginTfa, [login, code])
            const userType: UserType = UserType[`${accessToken.sub_type}`.toUpperCase() as keyof typeof UserType]
            yield call(getUserLogged, userType, `${accessToken.sub}`)
            yield put(loginTfaSuccess())
        }
    } catch (err: any) {
        yield put(loginTfaFailure())
        yield put(openSnackBar({
            title: 'Não autorizado.',
            type: SnackBarMessageType.WARNING,
            message: `${Messages.REQUEST.AUTH.CODE_INVALID}`
        }))
    }
}

export function* setupTfa(action: PayloadAction<IActionAuth>) {
    const { setupTfaSuccess, setupTfaFailure } = AuthActions
    const { openSnackBar } = SnackbarActions
    try {
        const { login } = action.payload
        const data = yield apply(authService, authService.setupTfa, [login])
        yield put(setupTfaSuccess({
            data
        }))
    } catch (err: any) {
        yield put(setupTfaFailure())
        if (err.message === 'Request failed with status code 401') {
            yield put(openSnackBar({
                title: 'Não autorizado.',
                type: SnackBarMessageType.WARNING,
                message: `${Messages.REQUEST.AUTH.CODE_INVALID}`
            }))
        }
        if (err.message === 'Request failed with status code 400') {
            yield put(openSnackBar({
                title: 'Email inválido.',
                type: SnackBarMessageType.WARNING,
                message: `${Messages.REQUEST.AUTH.EMAIL_NULL}`
            }))
        }
        if (err.message === 'An unexpected error has occurred. Please try again later...') {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

const authSaga = function* () {
    yield all([
        takeLatest(AuthTypes.LOGIN_REQUEST, authenticate),
        takeLatest(AuthTypes.REPLACE_PASSWORD, replacePassword),
        takeLatest(AuthTypes.FORGOT_PASSWORD, forgotPassword),
        takeLatest(AuthTypes.LOGIN_TFA, loginTfa),
        takeLatest(AuthTypes.SETUP_TFA, setupTfa),
    ])
}

export default authSaga
